import { useState, type ReactNode } from "react";
import { cn } from "../lib/utils.js";

const SIZES = {
  // 16px — presence viewer chips on inbox rows / thread header (§5.3).
  xs: "h-4 w-4 text-[10px]",
  sm: "h-7 w-7 text-[10px]",
  md: "h-8 w-8 text-[11px]",
  lg: "h-16 w-16 text-xl",
} as const;

interface ContactAvatarProps {
  /** Display name — drives the initials fallback and the alt text. */
  name: string;
  /** Re-hosted public avatar URL, or null when the contact has none. */
  avatarUrl?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
  /** Small overlay rendered bottom-right — e.g. a channel-type badge. */
  badge?: ReactNode;
}

/**
 * A contact's profile photo, captured from the social channel they
 * first reached the dealership on. Falls back to two-letter initials
 * on a tinted disc when no avatar was captured — the normal case for
 * WhatsApp / plain phone, or when a capture failed.
 */
export function ContactAvatar({
  name,
  avatarUrl,
  size = "md",
  className,
  badge,
}: ContactAvatarProps) {
  // A dead URL (expired social CDN link) falls back to initials instead
  // of the browser's broken-image glyph. Keyed by URL so a new avatar
  // for the same contact gets another chance to load.
  const [erroredUrl, setErroredUrl] = useState<string | null>(null);
  return (
    <span className={cn("relative inline-flex shrink-0", className)}>
      {avatarUrl && avatarUrl !== erroredUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          onError={() => setErroredUrl(avatarUrl)}
          className={cn("shrink-0 rounded-full object-cover", SIZES[size])}
        />
      ) : (
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full bg-primary/15 font-bold uppercase text-primary",
            SIZES[size],
          )}
          aria-label={name}
        >
          {size === "xs" ? initialsOf(name).charAt(0) : initialsOf(name)}
        </span>
      )}
      {badge ? (
        <span className="absolute -bottom-1 -right-1 flex">{badge}</span>
      ) : null}
    </span>
  );
}

/**
 * Two-letter initials from a display name. Multi-word → first letter
 * of each of the first two words. Single-word → first two letters.
 */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
